import type { ChangeEvent } from 'react';
import { slugify } from '../lib/slugify';
import { Tooltip, type TooltipValue } from './Tooltip';

export interface SliderProps {
  label: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  onChange: (value: number) => void;
  /** Optional fixed-decimal format for the value pill (default 2). */
  precision?: number;
  /** Optional unit shown after the value (e.g. "%"). */
  unit?: string;
  /** aria-description for screen readers. */
  description?: string;
  /**
   * v6 — tooltip shown on hover/focus next to the label. May be a plain
   * string (used in both language modes) OR a per-mode pair so the
   * tooltip flips between plain-language and technical JSDoc strings.
   */
  tooltip?: TooltipValue;
}

/**
 * Accessible slider with a visible value pill and ≥44px hit target on the
 * thumb. The label sits above the track; the pill right-aligns.
 *
 * v6 — the label is wrapped in a `<Tooltip>` so hovering (or tabbing
 * through) reveals a description of what this control does and what
 * changing it looks like. The tooltip text is language-aware.
 */
export function Slider(props: SliderProps) {
  const {
    label,
    value,
    min,
    max,
    step = 0.01,
    onChange,
    precision = 2,
    unit,
    description,
    tooltip,
  } = props;

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const next = Number(e.target.value);
    if (!Number.isNaN(next)) onChange(next);
  };

  const formatted = `${value.toFixed(precision)}${unit ?? ''}`;

  const labelNode = (
    <label
      htmlFor={`slider-${slugify(label)}`}
      className="text-sm font-medium text-neutral-800 dark:text-neutral-200"
    >
      {label}
    </label>
  );

  return (
    <div className="block">
      <div className="flex items-baseline justify-between gap-2">
        {tooltip ? <Tooltip value={tooltip}>{labelNode}</Tooltip> : labelNode}
        <span
          className="rounded-md bg-neutral-200 px-2 py-0.5 font-mono text-xs tabular-nums text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300"
          aria-hidden="true"
        >
          {formatted}
        </span>
      </div>
      <input
        id={`slider-${slugify(label)}`}
        name={`slider-${slugify(label)}`}
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={handleChange}
        aria-label={description ?? label}
        aria-valuetext={description ?? formatted}
        title={description ?? label}
        className="mt-2 h-11 w-full cursor-pointer appearance-none rounded-full bg-neutral-200 accent-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-1 dark:bg-neutral-800"
      />
    </div>
  );
}
